import {chunk, last, memoize} from 'lodash';
import config from '../config';
import {dirCache} from '../model/dirCache';
import {getDistanceInMetres} from './routes-lib';
import {ulid} from 'ulid';
import {find} from 'lodash/collection';
import {toKmMs} from './formatLib';
import debounce from 'debounce-promise';
import {getDistance, getGreatCircleBearing} from 'geolib';
import restrictedPoints from '../model/restrictedPoints';
import store from "store2";
import crypto from 'crypto';

const interpolatePoint = (point1, point2, t) => ({
    lat: point1.lat + (point2.lat - point1.lat) * t,
    lon: point1.lon + (point2.lon - point1.lon) * t,
});

// Function to ensure equal number of waypoints
const normalisePaths = (path1, path2) => {
    const length1 = path1.length;
    const length2 = path2.length;

    // Determine the longer and shorter paths
    let [longerPath, shorterPath] = length1 >= length2 ? [path1, path2] : [path2, path1];

    // Calculate the number of waypoints to add
    const targetLength = longerPath.length;
    const segments = shorterPath.length - 1;
    const newPath = [];

    // Add interpolated waypoints to the shorter path
    for (let i = 0; i < targetLength; i++) {
        const ratio = i / (targetLength - 1); // Ratio of current position in the new path

        let segmentIndex = Math.floor(ratio * segments);
        if (segmentIndex >= shorterPath.length - 1) {
            segmentIndex--;
        }
        const t = (ratio * segments) - segmentIndex;

        // Interpolate between two waypoints
        const interpolated = interpolatePoint(shorterPath[segmentIndex], shorterPath[segmentIndex + 1], t);

        newPath.push(interpolated);
    }

    return {
        path1: length1 >= length2 ? path1 : newPath,
        path2: length1 >= length2 ? newPath : path2
    };
};

function breakIntoSegments(path, minSegmentLength) {
    const segments = [];
    let currentSegment = [path[0]];

    for (let i = 1; i < path.length; i++) {
        currentSegment.push(path[i]);
        const segmentDistance = getDistance(currentSegment[0], currentSegment[currentSegment.length - 1]);

        if (segmentDistance >= minSegmentLength) {
            segments.push(currentSegment);
            currentSegment = [path[i]];
        }
    }

    if (currentSegment.length > 1) {
        segments.push(currentSegment);
    }

    return segments;
}


// Function to interpolate points along a path
function interpolatePath(path, interval) {
    const interpolatedPath = [path[0]];
    let lastPoint = path[0];
    let accumulatedDistance = 0;

    for (let i = 1; i < path.length; i++) {
        const segmentDistance = getDistance(lastPoint, path[i]);
        accumulatedDistance += segmentDistance;

        while (accumulatedDistance >= interval) {
            const ratio = (interval - (accumulatedDistance - segmentDistance)) / segmentDistance;
            const interpolatedPoint = {
                lat: lastPoint.lat + ratio * (path[i].lat - lastPoint.lat),
                lon: lastPoint.lon + ratio * (path[i].lon - lastPoint.lon)
            };
            interpolatedPath.push(interpolatedPoint);
            accumulatedDistance -= interval;
            lastPoint = interpolatedPoint;
        }

        lastPoint = path[i];
    }

    return interpolatedPath;
}

// Function to make paths have the same number of waypoints
export function makePathsComparable(path1, path2, interval = 100) {
    const interpolatedPath1 = interpolatePath(path1, interval);
    const interpolatedPath2 = interpolatePath(path2, interval);
    const minLength = Math.min(interpolatedPath1.length, interpolatedPath2.length);

    return {
        path1: interpolatedPath1.slice(0, minLength),
        path2: interpolatedPath2.slice(0, minLength)
    };
}

export function findDivergenceByDistance(pathA, pathB, maxDistBetweenPoints = 10, segmentLength = 10, distanceThreshold = 50) {
    const interpolatedPathA = interpolatePath(pathA, maxDistBetweenPoints);
    const {path1, path2} = normalisePaths(interpolatedPathA, pathB);
    const segments1 = breakIntoSegments(path1, segmentLength);
    const segments2 = breakIntoSegments(path2, segmentLength);
    const minLength = Math.min(segments1.length, segments2.length);

    for (let i = 0; i < minLength; i++) {
        const segment1 = segments1[i];
        const segment2 = segments2[i];
        const lastSegment1Wp = last(segment1);
        const lastSegment2Wp = last(segment2);


        const distance = getDistance(lastSegment1Wp, lastSegment2Wp);

        if (distance > distanceThreshold) {
            const nearestWaypoint = pathA.reduce((current, next, i) => {
                const nextDistance = getDistance(lastSegment1Wp, next);
                if (!current.nearest || nextDistance < current.distance) {
                    current.distance = nextDistance;
                    current.nearest = next;
                    current.index = i
                }
                return current;
            }, {})

            // Already made this segment a chekpoint, so need to move to next segment
            if (nearestWaypoint?.nearest?.checkpoint) {
                continue;
            }
            //find(pathA, wp => wp.lat === lastSegmentWp.lat && wp.lon === lastSegmentWp.lon)
            return {nearestWaypoint, segment1, segment2};
        }
    }

    return null; // Paths do not diverge within the length of the shorter path
}

export function findDivergenceByBearing(pathA, pathB, maxDistBetweenPoints = 10, segmentLength = 50, bearingThreshold = 5) {
    const interpolatedPathA = interpolatePath(pathA, maxDistBetweenPoints);
    const {path1, path2} = normalisePaths(interpolatedPathA, pathB);
    const segments1 = breakIntoSegments(path1, segmentLength);
    const segments2 = breakIntoSegments(path2, segmentLength);
    const minLength = Math.min(segments1.length, segments2.length);

    let waypoint;
    for (let i = 0; i < minLength; i++) {
        const segment1 = segments1[i];
        const segment2 = segments2[i];

        const bearing1 = getGreatCircleBearing(segment1[0], segment1[segment1.length - 1]);
        const bearing2 = getGreatCircleBearing(segment2[0], segment2[segment2.length - 1]);

        if (Math.abs(bearing1 - bearing2) > bearingThreshold) {
            const lastSegmentWp = last(segment1);
            const nearestWaypoint = pathA.reduce((current, next, i) => {
                const nextDistance = getDistance(lastSegmentWp, next);
                if (!current.nearest || nextDistance < current.distance) {
                    current.distance = nextDistance;
                    current.nearest = next;
                    current.index = i;
                }
                return current;
            }, {nearest: null, index: -1})
            //find(pathA, wp => wp.lat === lastSegmentWp.lat && wp.lon === lastSegmentWp.lon)
            return {nearestWaypoint, segment1, segment2};
        }
    }

    return null; // Paths do not diverge within the length of the shorter path
}

export const toCoordStr = (coord, precision = 5) => `${coord.lon?.toFixed?.(precision)},${coord.lat?.toFixed?.(precision)}`;
export const toCoordArray = (coord, precision = 5) => [parseFloat(coord.lon.toFixed(precision)), parseFloat(coord.lat.toFixed(precision))];
export const getSimpleDurations = (profile, startCoord, coordinates, speed) => {
    const metresPerSec = speed / 3.6;
    const results = coordinates.map(coord => {
        const distance = getDistanceInMetres(startCoord, coord);
        const duration = distance / metresPerSec;
        return {duration, distance};
    });
    return {durations: results.map(result => result.duration), distances: results.map(result => result.distance)};
};
export const getSimpleDurationsBetweenStops = (profile, coords, speed = 4.5) => {
    const metresPerSec = speed / 3.6;
    const distances = [], durations = [];
    coords.forEach((coord1, idx) => {
        distances[idx] = [];
        durations[idx] = [];
        coords.forEach(coord2 => {
            const distance = getDistanceInMetres(coord1, coord2);
            const duration = distance / metresPerSec;
            distances[idx].push(distance);
            durations[idx].push(duration);
        });
    });
    return {durations: durations, distances: distances};
};
export const getAccurateDurationsMapbox = async (profile, startCoord, coordinates, fallbackSpeed = 5) => {

    const uniqueCoordResults = {};
    coordinates.map(c => toCoordStr(c)).forEach(coord => uniqueCoordResults[coord] = -1);

    if (Object.keys(uniqueCoordResults).length === 1 && toCoordStr(startCoord) === Object.keys(uniqueCoordResults)[0]) {
        console.log('Coord the same as start.');
        return {durations: coordinates.map(_ => 0), distances: coordinates.map(_ => 0)};
    }

    await Promise.all(chunk(Object.keys(uniqueCoordResults), 24).map(async coords => {
        //https://api.mapbox.com/directions-matrix/v1/{profile}/{coordinates}
        return fetch('https://api.mapbox.com/directions-matrix/v1/mapbox/' + profile.toLowerCase() + '/' + toCoordStr(startCoord) + ';' + coords.join(';') + '?fallback_speed=' + fallbackSpeed + '&sources=0&annotations=distance,duration&access_token=' + config.maps.mabBox, {
            method: 'GET'
        })
            .then(response => response.json())
            .then(data => {
                if (data.code === 'Ok') {
                    const durations = data.durations[0].slice(1).map(d => Math.ceil(d / 60) * 60);
                    const distances = data.distances[0].slice(1).map(d => Math.round(d / 10) * 10);
                    coords.forEach((coord, idx) => (uniqueCoordResults[coord] = {
                        duration: durations[idx],
                        distance: distances[idx]
                    }));
                }
            });
    }));

    const mappedResults = coordinates.map(c => toCoordStr(c)).map(coord => uniqueCoordResults[coord]);
    return {
        durations: mappedResults.map(result => result.duration),
        distances: mappedResults.map(result => result.distance)
    };
};
export const getAccurateDurationsGeoApify = async (profile, startCoord, coordinates, fallbackSpeed = 5, apiKey = config.maps.geoapify.ctrxs) => {

    const uniqueCoordResults = {};
    coordinates.map(c => toCoordStr(c)).forEach(coord => uniqueCoordResults[coord] = -1);

    if (Object.keys(uniqueCoordResults).length === 1 && toCoordStr(startCoord) === Object.keys(uniqueCoordResults)[0]) {
        console.log('Coord the same as start.');
        return {durations: coordinates.map(_ => 0), distances: coordinates.map(_ => 0)};
    }

    await Promise.all(chunk(Object.keys(uniqueCoordResults), 10).map(async coords => {

        const data = {
            mode: profile.toLowerCase() === 'walking' ? 'walk' : 'drive',
            sources: [{location: [startCoord.lon, startCoord.lat]}],
            targets: coords.map(coord => ({location: coord.split(',').map(parseFloat)}))
        };
        const myHeaders = new Headers();
        myHeaders.append('Content-Type', 'application/json');

        console.log('Getting durations from GEOAPIFY');

        return fetch('https://api.geoapify.com/v1/routematrix?apiKey=' + apiKey, {
            method: 'POST',
            headers: myHeaders,
            body: JSON.stringify(data)
        })
            .then(response => response.json())
            .then(data => {
                data.sources_to_targets[0].forEach(data => {
                    const duration = Math.ceil(data.time / 60) * 60;
                    const distance = Math.round(data.distance / 10) * 10;
                    uniqueCoordResults[coords[data.target_index]] = {duration, distance};
                });
            });
    }));

    const mappedResults = coordinates.map(c => toCoordStr(c)).map(coord => uniqueCoordResults[coord]);
    return {
        durations: mappedResults.map(result => result.duration),
        distances: mappedResults.map(result => result.distance)
    };
};
export const getAccurateDurationsBetweenStops = async (profile, coordinates, fallbackSpeed = 4.5) => {
    //https://api.mapbox.com/directions-matrix/v1/{profile}/{coordinates}
    return fetch('https://api.mapbox.com/directions-matrix/v1/mapbox/' + profile.toLowerCase() + '/' + coordinates.map(c => toCoordStr(c)).join(';') + '?fallback_speed=' + fallbackSpeed + '&sources=0&annotations=distance,duration&access_token=' + config.maps.mabBox, {
        method: 'GET'
    })
        .then(response => response.json())
        .then(data => {
            if (data.code === 'Ok') {
                return data;
            }
        });
};
export const getPathMapbox = async (profile, coords, speedKmHr) => {
    coords = coords.map(coord => toCoordStr(coord));
    console.log('Getting path from %s to %s @ %dkm/hr', coords, speedKmHr);
    // const cacheDir = dirCache.getData(profile, coords, speedKmHr);
    // if (cacheDir) {
    //     return cacheDir;
    // }

    let walkingSpeedStr = '';
    if (speedKmHr) {
        walkingSpeedStr = 'walking_speed=' + (speedKmHr / 3.6);
    }


    //https://api.mapbox.com/directions/v1/{profile}/{coordinates}
    return fetch('https://api.mapbox.com/directions/v5/mapbox/' + profile.toLowerCase() + '/' + encodeURIComponent(coords.join(';')) + '?' + walkingSpeedStr + '&approaches='+encodeURIComponent(coords.map(()=>'curb').join(';'))+'&annotations=duration&geometries=geojson&access_token=' + config.maps.mabBox, {
        method: 'GET'
    })
        .then(response => response.json())
        .then(data => {
            if (data.code === 'Ok') {
                const waypoints = data.routes[0].geometry.coordinates.map(coords => ({lat: coords[1], lon: coords[0]}));
                const dir = {
                    waypoints,
                    duration: data.routes[0].duration,
                    distance: data.routes[0].distance,
                    geojson: {
                        type: 'Feature', geometry: {
                            type: 'LineString', coordinates: data.routes[0].geometry.coordinates
                        }
                    }
                };
                // dirCache.setData(profile, startCoord, endCoord, speedKmHr, dir);
                return dir;
            }
            return;
        });
};
export const getPathGeoApify = async (...args) => {
    let profile, startCoord, endCoord, speedKmHr, apiKey, avoid = null;
    if(typeof args[0] === 'object') {
        profile = args[0].profile;
        startCoord = args[0].startCoord;
        endCoord = args[0].endCoord;
        avoid = args[0].avoid;
        speedKmHr = args[0].speedKmHr;
        apiKey = args[0].apiKey;
    } else {
        profile = args[0];
        startCoord = args[1];
        endCoord = args[2];
        avoid = args[3];
        speedKmHr = args[4];
        apiKey = args[5];
    }

    // Hack for functions that didn't get the memo about the avoid param
    if (isFinite(avoid)) {
        avoid = null;
        speedKmHr = args[3];
        apiKey = args[4];
    }

    return getCoordsPathGeoApify({
        profile,
        coords: [startCoord, endCoord],
        speedKmHr,
        apiKey,
        avoid
    });
};

export const getCoordsPathGeoApify = async ({
    profile = 'bus',
    coords,
    speedKmHr = null,
    avoid = null,
    apiKey = config.maps.geoapify.ctrxs
} = {}) => {
    if (coords?.length < 2) {
        console.log('Need at least start and end coord.');
        return;
    }
    const coordsAsStr = coords.map(coord => toCoordStr(coord));
    console.log('Getting geoapify path along %s  @ %dkm/hr', coordsAsStr, speedKmHr);

    let avoidQuery = restrictedPoints.getGeoapifyAvoidQueryParam(coords)
    console.log('Avoid query param: ', avoidQuery);
    if (avoid?.lat && avoid?.lon) {
        console.log('Avoiding manually added point: ', avoid);
        avoidQuery = `${avoidQuery ? avoidQuery + '|' : '' }location:${avoid.lat},${avoid.lon}`;
    }
    console.log('Full Avoid query param: ', avoidQuery);
    const straightPath = () => {
        // const start = {lat: startCoord.lat, lon: startCoord.lon}
        // const end = {lat: endCoord.lat, lon: endCoord.lon}

        const distance = coords.reduce((p, c, i) => {
            if (i === 0) {
                return 0;
            }
            return p + getDistanceInMetres(p, c);
        }, 0);
        // const distance = getDistanceInMetres(start, end);

        const duration = distance / 14;
        last(coords).delta = duration;
        last(coords).distance = distance;
        return {waypoints: coords.map(c => ({lat: c.lat, lon: c.lon})), duration, distance};
    };

    const wpQueryCoords = coordsAsStr.map(c => `lonlat:${c}`);

    const mode = profile.toLowerCase() === 'walking' ? 'walk' : profile.toLowerCase() === 'driving' ? 'drive' : profile.toLowerCase();
    const url = 'https://api.geoapify.com/v1/routing?mode=' + mode + '&waypoints=' + wpQueryCoords.join('|') + (avoidQuery?.length ? '&avoid=' + avoidQuery : '') + '&traffic=approximated&type=less_maneuvers&apiKey=' + apiKey;

    console.log(url);

    //https://api.geoapify.com/v1/routing?
    return fetch(url, {
        method: 'GET'
    })
        .then(response => response.json())
        .then(data => {
            try {
                console.log('Got route path data from geoapify', data);
                if (data.error) {
                    console.log(data);
                    return straightPath();
                }

                const time = data.features[0].properties.time;
                const dist = data.features[0].properties.distance;
                const avgSpd = dist / parseFloat(time);
                console.log('Avg Spd: ' + avgSpd);

                let prevWp = null, waypoints = [];
                data.features[0].geometry.coordinates.forEach(coords => {
                    coords.forEach(coord => {
                        const wp = {
                            lat: coord[1],
                            lon: coord[0], delta: 0, distance: 0
                        };
                        if (prevWp?.lat === wp.lat && prevWp?.lon === wp.lon) {
                            return;
                        }
                        if (prevWp) {
                            const dist = getDistanceInMetres(prevWp, wp);
                            wp.delta = Math.round(dist / avgSpd) + prevWp.delta;
                            wp.distance = dist + prevWp.distance;
                        }
                        prevWp = wp;
                        waypoints.push(wp);
                    });
                });

                // let waypoints = data.features[0].geometry.coordinates[0].map((coords, idx) => {
                //     const wp = {
                //         lat: coords[1],
                //         lon: coords[0], delta: 0, distance: 0
                //     };
                //     if (prevWp) {
                //         const dist = getDistanceInMetres(prevWp, wp);
                //         wp.delta = Math.round(dist / avgSpd) + prevWp.delta;
                //         wp.distance = dist + prevWp.distance
                //     }
                //     prevWp = wp;
                //     return wp;
                // });
                const path = {
                    waypoints,
                    duration: data.features[0].properties.time,
                    distance: data.features[0].properties.distance,
                    geojson: data
                };
                console.log(path);
                return path;
            } catch (e) {
                console.log(e);
            }
        }).catch(e => {
            console.log(e, e);
            return straightPath();
        });
};
export const geocodeGeoApify = async (search, apiKey = config.maps.geoapify.ctrxs) => {
    if (config.local) {
        return Array.from(Array(5).keys()).map(min => ({
            lat: min, lon: min,
            formatted: 'Bargo ' + min,
            place_id: ulid(),
            place_name: 'Bargo ' + min,
            center: [min, min]
        })).filter(p => p.formatted.toLowerCase().includes(search.toLowerCase()));
    }
    const url = `https://api.geoapify.com/v1/geocode/search?text=${search}&format=json&filter=countrycode:au&apiKey=${apiKey}`;
    return fetch(url, {
        method: 'GET'
    })
        .then(response => response.json())
        .then(result => {
            return result.results.map(r => {
                r.place_name = r.formatted;
                r.center = [r.lon, r.lat];
                return r;
            });
        })
        .catch(error => console.log('error looking up geocode', error));
};

export const getAddressGeoApify = async (search, apiKey = config.maps.geoapify.ctrxs) => {
    if (config.local) {
        return Array.from(Array(5).keys()).map(min => ({
            lat: min, lon: min,
            formatted: 'Bargo ' + min,
            place_id: ulid(),
            place_name: 'Bargo ' + min,
            center: [min, min]
        })).filter(p => p.formatted.toLowerCase().includes(search.toLowerCase()));

    }
    const url = `https://api.geoapify.com/v1/geocode/autocomplete?text=${search}&format=json&filter=countrycode:au&apiKey=${apiKey}`;
    return fetch(url, {
        method: 'GET'
    })
        .then(response => response.json())
        .then(result => {
            return result.results.map(r => {
                r.place_name = r.formatted;
                r.center = [r.lon, r.lat];
                return r;
            });
        })
        .catch(error => console.log('error looking up geocode', error));
};

function calculateDir(deg1, deg2) {
    return (360 + deg1 - deg2) % 360 > 180 ? '(R)' : '(L)';
}

function getMinTurnAngle(config) {
    return isFinite(config?.operator?.opts?.shiftbat?.minTurnAngle) && config?.operator?.opts?.shiftbat?.minTurnAngle >= 0 ? config?.operator?.opts?.shiftbat?.minTurnAngle : 30;
}

export const getDirections = async (wps, apiKey = config.maps.geoapify.ctrxs, force = false) => {
    const wpsHash = crypto.createHash('sha256').update(JSON.stringify(wps.map(wp => [wp.lon, wp.lat]))).digest('hex');
    const cacheKey = `directions:${apiKey}:${wpsHash}`;
    if(!force) {
        const cached = store.session.get(cacheKey);
        if (cached) {
            try {
                const parsed = JSON.parse(cached);
                if (parsed && parsed.directions && parsed.waypoints) {
                    return parsed;
                }
            } catch (e) {
                store.session.remove(cacheKey);
                console.log('Error parsing cached directions', e);
            }
        }
    }

    const body = {'mode': 'drive', 'waypoints': wps.map(wp => ({location: [wp.lon, wp.lat]}))};
    try {
        return fetch('https://api.geoapify.com/v1/mapmatching?apiKey=' + apiKey, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
        })
            .then(res => res.json())
            .then(data => {
                if (!data.features?.length) {
                    return;
                }

                const legs = data.features[0]?.properties?.legs;
                let currentSt = legs[0].steps[legs[0].steps.filter(step => !step.roundabout).findIndex(step => step.name)].name;
                let prevBearing = null;
                let distance = 0;
                const directions = [], waypoints = data.features[0]?.properties?.waypoints;
                legs.forEach((leg, idx, legsArr) => {
                    leg.steps.filter(step => !step.roundabout).forEach((step, ix, stepsArr) => {
                        const st = step.name;
                        if (st === currentSt) {
                            prevBearing = step.end_bearing;
                            distance += step.distance;
                            return;
                        }
                        currentSt = st;
                        const newBearing = step.begin_bearing;
                        const diff = newBearing - prevBearing;

                        if ((st?.length || distance > 0) && Math.abs(diff) > getMinTurnAngle(config)) {
                            directions.push(`${calculateDir(prevBearing, newBearing)} ${!st?.length ? `in ${toKmMs(distance, 0)}` : find(st.split(','), name => !name.match(/\d+/g)) || st.split(',')[0]}`);
                        }
                        prevBearing = step.end_bearing;
                    });
                    distance = 0;
                });
                try {
                    store.session.set(cacheKey, JSON.stringify({directions, waypoints}));
                    console.log('Cached directions', cacheKey);
                } catch (e) {
                    console.log('Error caching directions', e);
                }
                return {directions, waypoints};
            }).catch(e => {
                console.log(e, e);
                console.log(body);
            });
    } catch (e) {
        console.log(body)
        console.log(e, e);
    }
};


export const getCachedSimpleDurations = memoize(getSimpleDurations, (...args) => {
    return [args[0], toCoordStr(args[1]), args[2].map(c => toCoordStr(c)).join(';'), args[3]].join('_');
});
export const getCachedAccurateDurations = memoize(getAccurateDurationsGeoApify, (...args) => {
    return [args[0], toCoordStr(args[1]), args[2].map(c => toCoordStr(c)).join(';'), args[3]].join('_');
});
export const getCachedSimpleDurationsBetweenStops = memoize(getSimpleDurationsBetweenStops, (...args) => {
    return [args[0], args[1].map(c => toCoordStr(c)).join(';'), args[2]].join('_');
});
export const getCachedAccurateDurationsBetweenStops = memoize(getAccurateDurationsBetweenStops, (...args) => {
    return [args[0], args[1].map(c => toCoordStr(c)).join(';'), args[2]].join('_');
});
export const getCachedPath = memoize(getPathGeoApify, (...args) => {
    let profile, startCoord, endCoord, speedKmHr, apiKey;
    if(typeof args[0] === 'object') {
        profile = args[0];
        startCoord = args[0].startCoord;
        endCoord = args[0].endCoord;
        speedKmHr = args[0].speedKmHr;
        apiKey = args[0].apiKey;
    } else {
        profile = args[0];
        startCoord = args[1];
        endCoord = args[2];
        speedKmHr = args[3];
        apiKey = args[4];
    }
    return [profile, toCoordStr(startCoord), toCoordStr(endCoord), speedKmHr, apiKey].join('_');
});

export const getCachedDirections = memoize(getDirections, (...args) => {
    return [args[0].map(coord => toCoordStr(coord))].join('_');
});

export const getCachedPathMapbox = memoize(getPathMapbox, (...args) => {
    return [args[0], args[1].map(coord => toCoordStr(coord)).join(';'), args[3]].join('_');
});
export const getCachedPathDebounced = debounce(({profile, startCoord, endCoord, speedKmHr, direction, apiKey}) =>
    getCachedPath({profile, startCoord, endCoord, speedKmHr, direction, apiKey}),
    100
);

export const getCachedAddress = memoize(getAddressGeoApify, (...args) => {
    return args[0];
});
export const getCachedAddressDebounced = debounce((search) => getCachedAddress(search), 100);

export const getCachedPathFromCoords = memoize(getCoordsPathGeoApify, (...args) => {
    return [args[0].profile, args[0].coords.map(coord => toCoordStr(coord)).join('|'), args[0].speedKmHr, args[0].direction, args[0].apiKey].join('_');
})
export const getCachedPathFromCoordsDebounced = debounce((type, coords, speed, avoid) => getCachedPathFromCoords({profile: type, coords, speedKmHr: speed, avoid}), 100);

export const getCachedGeocode = memoize(geocodeGeoApify, (...args) => {
    return args[0];
});

export const snapToRoadOsrm = async ({lat, lon}) => {
    const url = `http://routing.openstreetmap.de/routed-bike/nearest/v1/walking/${lon},${lat}`;
  
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("Network response was not ok");
  
      const data = await response.json();
      const snapped = data.waypoints?.[0]?.location; // [lon, lat]
  
      if (!snapped) throw new Error("No snapped location found");
  
      return {
        lat: snapped[1],
        lon: snapped[0],
      };
    } catch (err) {
      console.error("Snap to road failed:", err);
      return null;
    }
  }

/**
 * Snaps points to the nearest road using Geoapify API
 * @param {Object|Array} points - Single point {lat,lon} or array of points to snap
 * @param {string} apiKey - Geoapify API key
 * @returns {Promise<Object|Array>} Snapped point(s)
 */
export const snapToRoad = async (points, apiKey = config.maps.geoapify.ctrxs) => {
    try {
        const singlePoint = !Array.isArray(points);
        // Handle single point
        if (singlePoint) {
            points = [points, points];
        }

        const requestBody = {
            mode: "drive",
            waypoints: points.map(point => ({
                location: [point.lon, point.lat]
            }))
        };

        const response = await fetch(`https://api.geoapify.com/v1/mapmatching?apiKey=${apiKey}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
        });
        const data = await response.json();

        if (!data.features?.length) {
            throw new Error('No snapped points returned');
        }

        // Extract snapped coordinates from matched waypoints
        const snappedPoints = data.features[0].properties.waypoints.map(waypoint => ({
            lat: waypoint.location[1],
            lon: waypoint.location[0]
        }));

        // Return single point or array based on input
        return singlePoint ? snappedPoints[0] : snappedPoints;

    } catch (error) {
        console.error('Error snapping to road:', error);
        return points; // Return original points on error
    }
};

/**
 * Snaps a point to the nearest road using HERE Route Matching API v8
 * @param {Object} point - The point to snap {lat, lon}
 * @param {string} apiKey - HERE API key (optional, will use config if not provided)
 * @returns {Promise<Object|null>} Snapped point {lat, lon} or null on error
 */
export const snapToRoadHere = async ({lat, lon}, apiKey = config.maps.here?.apiKey) => {
    if (!apiKey) {
        console.error("HERE API key not provided and not found in config");
        return null;
    }

    // HERE Route Matching API v8 endpoint
    // Using the compatibility endpoint that matches v2 format
    const url = `https://routematching.hereapi.com/v8/match/routelinks?routeMatch=1&mode=car&apiKey=${apiKey}`;
  
    try {
        // Create the request body for route matching
        const requestBody = `LATITUDE,LONGITUDE\n${lat},${lon}`;

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'text/csv'
            },
            body: requestBody
        });
        
        if (!response.ok) throw new Error("Network response was not ok");
  
        const data = await response.json();
        
        // Check if we have valid match results
        if (!data.response?.route?.length) {
            throw new Error("No match results returned from HERE Route Matching API");
        }

        // Extract the matched point from the first result
        const matchedWaypoint = data.response.route[0]?.waypoint?.[0];
        if (!matchedWaypoint) {
            throw new Error("No matched route found in results");
        }

        const mappedPosition = matchedWaypoint.mappedPosition;
        if (!mappedPosition.latitude || !mappedPosition.longitude) {
            throw new Error("No valid matched point found");
        }
  
        return {
            lat: mappedPosition.latitude,
            lon: mappedPosition.longitude,
        };
    } catch (err) {
        console.error("HERE snap to road failed:", err);
        return null;
    }
}

/**
 * Snaps multiple points to the nearest road using HERE Routing API
 * @param {Array} points - Array of points to snap [{lat, lon}, ...]
 * @param {string} apiKey - HERE API key (optional, will use config if not provided)
 * @returns {Promise<Array|null>} Array of snapped points or null on error
 */
export const snapToRoadHereBatch = async (points, apiKey = config.maps.here?.apiKey) => {
    if (!apiKey) {
        console.error("HERE API key not provided and not found in config");
        return null;
    }

    if (!Array.isArray(points) || points.length === 0) {
        console.error("Invalid points array provided");
        return null;
    }

    try {
        // Create waypoints string for HERE API
        const waypoints = points.map(point => `${point.lat},${point.lon}`).join('&destination=');
        const url = `https://router.hereapi.com/v8/route?transportMode=car&origin=${waypoints}&return=polyline,summary&apiKey=${apiKey}`;

        const response = await fetch(url);
        if (!response.ok) throw new Error("Network response was not ok");

        const data = await response.json();

        if (!data.routes || !data.routes.length || !data.routes[0].sections) {
            throw new Error("No route data returned from HERE API");
        }

        // Extract waypoints from each section
        const snappedPoints = data.routes[0].sections.map(section => {
            if (section.departure && section.departure.lat && section.departure.lng) {
                return {
                    lat: section.departure.lat,
                    lon: section.departure.lng,
                };
            }
            return null;
        }).filter(point => point !== null);

        return snappedPoints.length > 0 ? snappedPoints : null;

    } catch (err) {
        console.error("HERE batch snap to road failed:", err);
        return null;
    }
}

/**
 * Extrapolates a point backwards along the vector defined by startPoint and endPoint.
 * @param {object} startPoint - The starting point { lat, lon }.
 * @param {object} endPoint - The ending point { lat, lon }.
 * @param {number} [distanceMeters=5] - The distance in meters to extrapolate backwards.
 * @returns {object|null} The extrapolated point { lat, lon }, or null if inputs are invalid.
 */
export const getExtrapolatedPoint = (startPoint, endPoint, distanceMeters = 5) => {
    if (!startPoint || !endPoint || !startPoint.lat || !startPoint.lon || !endPoint.lat || !endPoint.lon) {
        console.error("Invalid input points for extrapolation.");
        return null;
    }

    // Calculate vector from startPoint to endPoint
    const dx = endPoint.lon - startPoint.lon;
    const dy = endPoint.lat - startPoint.lat;

    // Get vector length
    const length = Math.sqrt(dx * dx + dy * dy);

    if (length === 0) {
        console.warn("Start and end points are the same, cannot extrapolate.");
        // Return the start point itself
        return { lat: startPoint.lat, lon: startPoint.lon };
    }

    // Approximate degree conversion: 1 degree latitude ≈ 111km
    const metersToDegreesLat = distanceMeters / 111000;
    // For longitude, it depends on latitude: 1 degree longitude ≈ 111km * cos(latitude)
    const metersToDegreesLon = distanceMeters / (111000 * Math.cos(startPoint.lat * Math.PI / 180));

    // Extrapolate *backwards* from startPoint
    const extrapolatedPoint = {
        lat: startPoint.lat - (dy / length) * metersToDegreesLat,
        lon: startPoint.lon - (dx / length) * metersToDegreesLon
    };

    return extrapolatedPoint;
};

export const getCachedSnapToRoad = memoize(snapToRoad, (...args) => {
    const points = args[0];
    const apiKey = args[1];
    
    // Handle both single point and array of points
    const coordsStr = Array.isArray(points) 
        ? points.map(p => toCoordStr(p)).join('|')
        : toCoordStr(points);
        
    return [coordsStr, apiKey].join('_');
});

export const getCachedSnapToRoadDebounced = debounce((points, apiKey) => 
    getCachedSnapToRoad(points, apiKey), 
    100
);

export const getCachedSnapToRoadHere = memoize(snapToRoadHere, (...args) => {
    const point = args[0];
    const apiKey = args[1];
    return [toCoordStr(point), apiKey].join('_');
});

export const getCachedSnapToRoadHereDebounced = debounce((point, apiKey) => 
    getCachedSnapToRoadHere(point, apiKey), 
    100
);

export const getCachedSnapToRoadHereBatch = memoize(snapToRoadHereBatch, (...args) => {
    const points = args[0];
    const apiKey = args[1];
    
    const coordsStr = points.map(p => toCoordStr(p)).join('|');
    return [coordsStr, apiKey].join('_');
});

export const getCachedSnapToRoadHereBatchDebounced = debounce((points, apiKey) => 
    getCachedSnapToRoadHereBatch(points, apiKey), 
    100
);